/*
  Maison Bagages : popup automatique (bloc mb-popup-auto). Documenté dans assets/CUSTOM.txt.
  Ouvre la fenêtre native du thème (dialog-component de popup.js, méthode publique open()) au premier
  déclencheur atteint : délai, pourcentage de défilement ou intention de sortie (souris hors de la page, ordinateur).
  Après fermeture : pause de N jours et onglet de rappel. Jamais après inscription (clé mb:inscrit, ou événement
  « mb-popup:done » émis par un enfant), jamais pour un profil Klaviyo identifié (cookie __kla_id).
*/
const SUBSCRIBED_KEY = 'mb:inscrit';
const DAY = 86400000;

const store = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* stockage indisponible : la fenêtre se comporte comme à la première visite */
    }
  },
};

function klaviyoIdentified() {
  const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
  if (!match) return false;
  try {
    const data = JSON.parse(atob(decodeURIComponent(match[1])));
    return Boolean(data && (data.$exchange_id || data.$email));
  } catch {
    return false;
  }
}

// Bandeau de consentement Shopify encore affiché, ou à venir (région qui l'exige, choix du visiteur pas encore fait).
// shouldShowBanner() dit seulement si la région exige un bandeau : le choix se lit dans currentVisitorConsent().
function cookieBannerPending() {
  const banner = document.getElementById('shopify-pc__banner');
  if (banner) {
    const style = getComputedStyle(banner);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }
  try {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.shouldShowBanner?.()) return false;
    const consent = privacy.currentVisitorConsent?.() || {};
    return (consent.analytics ?? '') === '' && (consent.marketing ?? '') === '';
  } catch {
    return false;
  }
}

class MbPopupAuto extends HTMLElement {
  #timer = null;
  #armed = false;
  #onScroll = null;
  #onMouseOut = null;
  #onEditorSelect = null;
  #onEditorDeselect = null;

  connectedCallback() {
    this.key = `mb-popup:${this.dataset.key}`;
    this.dialogComponent = this.querySelector('dialog-component');
    this.modal = this.querySelector('[data-ref="popup-modal"]');
    this.tab = this.querySelector('[data-ref="mb-popup-tab"]');
    if (!this.dialogComponent || !this.modal) return;

    this.modal.addEventListener('close', () => this.#handleClose());
    this.tab?.addEventListener('click', () => this.open());
    // Les sections du thème posent un confinement qui capte position: fixed : l'onglet vit sous <body>.
    if (this.tab && this.tab.parentElement !== document.body) document.body.append(this.tab);
    this.addEventListener('mb-popup:done', () => this.#markDone());

    if (window.Shopify?.designMode) {
      this.#bindEditor();
      return;
    }

    if (getComputedStyle(this).display === 'none') return;
    if (this.#isDone()) return;
    if (this.dataset.hideIdentified === 'true' && klaviyoIdentified()) return;

    if (this.#isSnoozed()) {
      this.#showTab();
      return;
    }

    this.#arm();
  }

  disconnectedCallback() {
    this.#disarm();
    this.tab?.remove();
    if (this.#onEditorSelect) document.removeEventListener('shopify:block:select', this.#onEditorSelect);
    if (this.#onEditorDeselect) document.removeEventListener('shopify:block:deselect', this.#onEditorDeselect);
  }

  open() {
    this.#disarm();
    this.#hideTab();
    if (!this.dialogComponent.isOpen) this.dialogComponent.open();
    // showModal() place le focus sur le premier lien (anneau visible) : on le pose sur la fenêtre elle-même,
    // la touche Tab mène ensuite au contenu.
    this.modal.focus({ preventScroll: true });
  }

  #arm() {
    const delay = Number(this.dataset.delay) || 0;
    const scroll = Number(this.dataset.scroll) || 0;
    const fire = () => this.#autoOpen();
    this.#armed = true;

    if (delay > 0) this.#timer = setTimeout(fire, delay * 1000);

    if (scroll > 0) {
      this.#onScroll = () => {
        const root = document.documentElement;
        const max = root.scrollHeight - root.clientHeight;
        if (max > 0 && (root.scrollTop / max) * 100 >= scroll) fire();
      };
      window.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    if (this.dataset.exit === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.#onMouseOut = (event) => {
        if (!event.relatedTarget && event.clientY <= 0) fire();
      };
      document.addEventListener('mouseout', this.#onMouseOut);
    }
  }

  #disarm() {
    this.#armed = false;
    clearTimeout(this.#timer);
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    if (this.#onMouseOut) document.removeEventListener('mouseout', this.#onMouseOut);
    this.#onScroll = null;
    this.#onMouseOut = null;
  }

  #autoOpen() {
    if (!this.#armed || this.#isDone()) return;

    // Ne pas couvrir le bandeau cookies, une autre fenêtre ni le panier ouvert : on retente un peu plus tard.
    if (cookieBannerPending()) {
      clearTimeout(this.#timer);
      document.addEventListener('visitorConsentCollected', () => {
        this.#timer = setTimeout(() => this.#autoOpen(), 1500);
      }, { once: true });
      return;
    }
    const busy = document.querySelector('dialog[open]') || document.querySelector('cart-drawer.is-open');
    if (busy) {
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#autoOpen(), 5000);
      return;
    }

    this.open();
  }

  #handleClose() {
    if (window.Shopify?.designMode || this.#isDone()) return;
    store.set(`${this.key}:until`, String(Date.now() + (Number(this.dataset.days) || 7) * DAY));
    this.#showTab();
  }

  #markDone() {
    store.set(`${this.key}:done`, '1');
    store.set(SUBSCRIBED_KEY, '1');
    this.#hideTab();
  }

  #isDone() {
    return store.get(`${this.key}:done`) === '1' || store.get(SUBSCRIBED_KEY) === '1';
  }

  #isSnoozed() {
    return Number(store.get(`${this.key}:until`)) > Date.now();
  }

  #showTab() {
    if (this.tab) this.tab.hidden = false;
  }

  #hideTab() {
    if (this.tab) this.tab.hidden = true;
  }

  #bindEditor() {
    const concerned = (event) => event.target === this || this.contains(event.target);
    this.#onEditorSelect = (event) => {
      if (concerned(event)) this.open();
    };
    this.#onEditorDeselect = (event) => {
      if (concerned(event) && this.dialogComponent.isOpen) this.dialogComponent.close();
    };
    document.addEventListener('shopify:block:select', this.#onEditorSelect);
    document.addEventListener('shopify:block:deselect', this.#onEditorDeselect);
  }
}

if (!customElements.get('mb-popup-auto')) {
  customElements.define('mb-popup-auto', MbPopupAuto);
}
